import "client-only"

import { v4 as uuidV4 } from "uuid"

export async function seapServiceControl(billCodeId: number) {
  if (!window.seapweb) {
    console.error("seapweb not available")
    return
  }

  try {
    await window.seapweb.setUserAuthenticated(true)
    await window.seapweb.setUserId(billCodeId.toString())
    await window.seapweb.setExternalId(uuidV4())
    await window.seapweb.closeToken()
  } catch (error) {
    console.error("Error in seapServiceControl", error)
  }
}

/**
 * Retrieves the device ID from SEAP with automatic retry logic.
 * This function will poll for the device ID until it's available, SEAP is ready,
 * or the maximum number of retries is reached.
 *
 * Use this function instead of getDeviceId() in production code to ensure
 * reliable device ID retrieval across all page load scenarios (hot reload, fresh load, etc.)
 *
 * @returns Promise that resolves to the device ID if found, undefined otherwise
 */
export async function getDeviceIdWithRetry(): Promise<string | undefined> {
  const maxRetries = 45
  const retryInterval = 1000

  let retryCount = 0

  const tryGetDeviceId = async (): Promise<string | undefined> => {
    console.log(`SEAP Device ID attempt ${retryCount + 1}/${maxRetries}`)

    // Check if SEAP is ready and try to get device ID
    const deviceId = await getDeviceId()

    if (deviceId) {
      console.log("✓ Device ID retrieved")
      return deviceId
    }

    // Retry if we haven't exceeded max attempts
    if (retryCount < maxRetries) {
      retryCount++

      console.log(
        `Retrying in ${retryInterval}ms... (${retryCount}/${maxRetries})`,
      )

      return new Promise((resolve) => {
        setTimeout(async () => resolve(await tryGetDeviceId()), retryInterval)
      })
    }

    console.warn("Max retries reached, no device ID available")
  }

  return await tryGetDeviceId()
}

/**
 * Attempts to retrieve the device ID from SEAP (seapweb) with a single try.
 * This is a low-level function. For production use, prefer getDeviceIdWithRetry.
 * @returns The device ID if available, undefined otherwise
 */
async function getDeviceId() {
  if (!window.seapweb) {
    console.error("XTN seapweb is not available")
    return
  }

  try {
    const deviceId = await window.seapweb.getData("DA.DeviceID")
    return deviceId.trim()
  } catch (error) {
    console.warn("getDeviceId - Failed to retrieve device ID:", error)
  }
}
